import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Persona, PersonaSession, capabilitiesFor } from '../personas';
import type { CapabilityValue } from '../personas';

/** Served by the backend persona module under the scaffolder plugin path. */
export const PERSONA_SESSION_PATH = '/persona/me';

export type PersonaSessionState = PersonaSession & {
  loading: boolean;
  error?: string;
  can: (capability: CapabilityValue) => boolean;
};

const CONSUMER_FALLBACK: PersonaSession = {
  personas: [Persona.Consumer],
  capabilities: capabilitiesFor([Persona.Consumer]),
  spokeAdminOf: [],
};

/**
 * Personas of the signed-in user, read from the backend rather than decided in
 * the browser. Presentation only — every route the personas hide is also
 * refused by the backend.
 */
export const usePersonaSession = (): PersonaSessionState => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<PersonaSession>(CONSUMER_FALLBACK);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      try {
        setLoading(true);
        setError(undefined);

        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const response = await fetchApi.fetch(
          `${baseUrl}${PERSONA_SESSION_PATH}`,
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = (await response.json()) as Partial<PersonaSession>;
        if (mounted) {
          const personas = data.personas?.length
            ? data.personas
            : CONSUMER_FALLBACK.personas;
          setSession({
            personas,
            capabilities: capabilitiesFor(personas),
            spokeAdminOf: Array.isArray(data.spokeAdminOf)
              ? data.spokeAdminOf
              : [],
          });
        }
      } catch (caught) {
        // Fail closed: an unresolvable session is a Consumer, never an Admin.
        if (mounted) {
          setSession(CONSUMER_FALLBACK);
          setError(caught instanceof Error ? caught.message : String(caught));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [discoveryApi, fetchApi]);

  const can = useCallback(
    (capability: CapabilityValue) => session.capabilities.includes(capability),
    [session],
  );

  return useMemo(
    () => ({ ...session, loading, error, can }),
    [session, loading, error, can],
  );
};
